// One band of a term's implementation-fee schedule (migration 0011).
//
// The Harris agreement's Attachment B, Table 2 is two of these:
//
//     "<= 75,000 accounts"  ->  ceiling 75,000, $25,000
//     "> 75,000 accounts"   ->  no ceiling,     $45,000
//
// so the form asks for a CEILING rather than a range. That is the whole trick
// of the model and the form says so: a band's lower edge is the next band down's
// ceiling, which is what makes a schedule with a gap or an overlap in it
// unwriteable rather than merely discouraged.
//
// `agreement_term_id` is immutable, and `currency` is not on the form at all —
// it is the parent term's, copied server-side, because "every band under a term
// shares one currency" is a foreign key rather than a convention.

import type { ReactNode } from "react";
import { AgreementImplFeeTierCreate, AgreementImplFeeTierUpdate } from "@crm/shared";
import type { AgreementImplFeeTier, Currency } from "@crm/shared";
import { useAgreementImplFeeTierMutations } from "../../api/mutations.js";
import { FormDialog } from "../../forms/FormDialog.js";
import { NumberField, ReadOnlyField, TextField } from "../../forms/fields.js";
import { optionalText, parseOptionalInteger } from "../../forms/parseInput.js";
import { useEditor, type Wire } from "../../forms/useEditor.js";
import { formatCount, formatMoney, minorToInput, parseMoneyToMinor } from "../../format/money.js";
import { formatCalendarDate } from "../../format/calendarDate.js";

interface TierDraft {
  agreement_term_id: string;
  max_accounts: string;
  fee: string;
  notes: string;
}

const LABELS = {
  agreement_term_id: "Term",
  max_accounts: "Up to (accounts)",
  fee_minor: "Implementation fee",
  notes: "Notes",
} as const;

function draftFromRecord(tier: AgreementImplFeeTier): TierDraft {
  return {
    agreement_term_id: tier.agreement_term_id,
    // Blank is the open-ended top band, not zero. `textOf` would render a null
    // as "" too, but spelling it out here is what keeps the round trip legible:
    // blank in, null out, and null means "everyone above the band below".
    max_accounts: tier.max_accounts === null ? "" : String(tier.max_accounts),
    fee: minorToInput(tier.fee_minor, tier.currency),
    notes: tier.notes ?? "",
  };
}

export interface AgreementImplFeeTierFormDialogProps {
  agreementTermId: string;
  /** For the read-only "which term is this" field. */
  termEffectiveDate: string;
  /** The term's declared `impl_fee_currency` — bands have no currency of their own. */
  currency: Currency;
  /** Absent when adding a band. */
  tier?: AgreementImplFeeTier;
  onClose: () => void;
}

export function AgreementImplFeeTierFormDialog({
  agreementTermId,
  termEffectiveDate,
  currency,
  tier,
  onClose,
}: AgreementImplFeeTierFormDialogProps): ReactNode {
  const mutations = useAgreementImplFeeTierMutations();
  const isNew = tier === undefined;

  const toWire = (draft: TierDraft): Wire => ({
    agreement_term_id: draft.agreement_term_id,
    // Blank -> null is load-bearing: it is how the open-ended top band is
    // expressed, and `parseOptionalInteger` is what keeps "" from becoming 0 —
    // a ceiling of zero would be a band that covers nobody.
    max_accounts: parseOptionalInteger(draft.max_accounts),
    fee_minor: parseMoneyToMinor(draft.fee, currency),
    notes: optionalText(draft.notes),
  });

  const formatValue = (field: string, value: unknown): string => {
    if (value === null || value === undefined) {
      return field === "max_accounts" ? "no ceiling" : "—";
    }
    if (typeof value !== "number") return String(value);
    return field === "fee_minor" ? formatMoney(value, currency) : formatCount(value);
  };

  const editor = useEditor<
    TierDraft,
    AgreementImplFeeTierCreate,
    AgreementImplFeeTierUpdate,
    AgreementImplFeeTier
  >({
    mode: tier === undefined ? { kind: "create" } : { kind: "update", record: tier },
    blankDraft: {
      agreement_term_id: agreementTermId,
      max_accounts: "",
      fee: "",
      notes: "",
    },
    draftFromRecord,
    toWire,
    createSchema: AgreementImplFeeTierCreate,
    updateSchema: AgreementImplFeeTierUpdate,
    create: (body, idempotencyKey) => mutations.create.mutateAsync({ body, idempotencyKey }),
    update: (id, body, version) => mutations.update.mutateAsync({ id, body, version }),
    fetchCurrent: mutations.fetchCurrent,
    onSaved: onClose,
  });

  const unbounded = editor.draft.max_accounts.trim() === "";

  return (
    <FormDialog
      open
      onClose={onClose}
      title={isNew ? "Add a fee band" : "Edit fee band"}
      description={
        isNew
          ? "A band prices the implementation package for end customers up to a given account count. Leave the ceiling blank for the open-ended top band — the one everyone above the highest ceiling falls into."
          : "A correction to a band restates nothing already billed: an implementation fee added as a term line states its own amount, and an invoice that billed it froze that amount when it went out."
      }
      submitLabel={isNew ? "Add band" : "Save changes"}
      editor={editor}
      labels={LABELS}
      formatValue={formatValue}
    >
      <ReadOnlyField
        label={LABELS.agreement_term_id}
        value={`Effective ${formatCalendarDate(termEffectiveDate)}`}
        reason="A band belongs to the term that priced it. Repricing at an annual review is a new term, not an edit to this one."
      />
      <NumberField
        label={LABELS.max_accounts}
        value={editor.draft.max_accounts}
        error={editor.errors["max_accounts"]}
        hint={
          unbounded
            ? "Blank — this is the open-ended top band, and every customer above the next-highest ceiling is priced here. A term may have only one."
            : "INCLUSIVE: a customer with exactly this many accounts is priced in this band. The band starts where the next one down ends, so there is no lower bound to enter."
        }
        onChange={(value) => editor.setField("max_accounts", value)}
      />
      <NumberField
        label={LABELS.fee_minor}
        required
        suffix={currency}
        hint="The FULL fee, before the revenue split. Our share is this term's implementation split of it."
        value={editor.draft.fee}
        error={editor.errors["fee_minor"]}
        onChange={(value) => editor.setField("fee", value)}
      />
      <TextField
        label={LABELS.notes}
        placeholder="Attachment B, Table 2"
        hint="Optional. Worth naming the schedule this band came from — it is what someone checks it against a year from now."
        value={editor.draft.notes}
        error={editor.errors["notes"]}
        onChange={(value) => editor.setField("notes", value)}
      />
    </FormDialog>
  );
}
